import { Autocomplete, TextField } from '@mui/material';
import { NewLocationEntry } from '../../../Api/ServiceRequest/serviceRequestApi';
import { MultiNewLocationInput } from '../MultiNewLocationInput';
import { LocationEndField } from './LocationEndField';
import {
  LOCATION_LIMIT,
  LOCATION_LIMIT_HELP,
  MOVE_REQUEST,
  newLocationLimitText,
  P2P_NEW_LOCATION_LIMIT
} from './serviceRequestModel';

interface LocationFieldsProps {
  disabled: boolean;
  requestType: string;
  isP2PProduct: boolean;
  options: string[];
  displayLocationCode: boolean;
  advancedNewLocationSettings?: boolean;
  activeTab: number;
  locationSelections: string[];
  onLocationSelectionsChange: (value: string[]) => void;
  locationA: string | null;
  locationZ: string | null;
  onLocationAChange: (value: string | null) => void;
  onLocationZChange: (value: string | null) => void;
  newLocations: NewLocationEntry[];
  onNewLocationsChange: (value: NewLocationEntry[]) => void;
}

const locationsLabel = (requestType: string, displayLocationCode: boolean) => {
  const move = requestType === MOVE_REQUEST;
  if (displayLocationCode) return move ? 'To: Location Code' : 'Location Code';
  return move ? 'To: Location' : 'Locations';
};

const endLabel = (end: 'A' | 'Z', displayLocationCode: boolean) =>
  displayLocationCode ? `Location Code - Location ${end}` : `Location ${end}`;

/** The location pickers: existing locations (To: Location for a Move) plus new ones to add, or the A and Z ends of a point-to-point product. */
export function LocationFields({
  disabled,
  requestType,
  isP2PProduct,
  options,
  displayLocationCode,
  advancedNewLocationSettings,
  activeTab,
  locationSelections,
  onLocationSelectionsChange,
  locationA,
  locationZ,
  onLocationAChange,
  onLocationZChange,
  newLocations,
  onNewLocationsChange
}: LocationFieldsProps) {
  const newLocationLimit = isP2PProduct ? P2P_NEW_LOCATION_LIMIT : LOCATION_LIMIT;

  return (
    <>
      {isP2PProduct ? (
        <>
          <LocationEndField
            disabled={disabled}
            options={options}
            value={locationA}
            label={endLabel('A', displayLocationCode)}
            onChange={onLocationAChange}
          />
          <LocationEndField
            disabled={disabled}
            options={options}
            value={locationZ}
            label={endLabel('Z', displayLocationCode)}
            onChange={onLocationZChange}
          />
        </>
      ) : (
        <div style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
          <Autocomplete
            multiple
            disabled={disabled}
            sx={{ width: '100%' }}
            size="small"
            options={options}
            value={locationSelections}
            onChange={(_, value) => onLocationSelectionsChange(value)}
            getOptionDisabled={(option) =>
              locationSelections.length >= LOCATION_LIMIT && !locationSelections.includes(option)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                helperText={locationSelections.length >= LOCATION_LIMIT && LOCATION_LIMIT_HELP}
                label={locationsLabel(requestType, displayLocationCode)}
              />
            )}
          />
        </div>
      )}
      <div style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
        <MultiNewLocationInput
          key={`${isP2PProduct ? 'p2p-new-locations' : 'new-locations'}-${activeTab}`}
          linkClassName="text-[indigo] text-sm"
          limit={newLocationLimit}
          limitText={newLocationLimitText(newLocationLimit)}
          onLocationsChange={onNewLocationsChange}
          showAdvancedSettings={advancedNewLocationSettings}
          initialLocations={newLocations}
        />
      </div>
    </>
  );
}
